import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isProduction, isStaging } from 'constants/env';
import { RouterUrl } from 'routes';
import { loadScriptOnce } from './useScriptGlobal';

const WEBITEL_WIDGET_ID = 'wt-omnichannel-widget';
const WEBITEL_CHAT_URL = process.env.REACT_APP_WEBITEL_CHAT_URL;
const WEBITEL_CHAT_WSS = process.env.REACT_APP_WEBITEL_CHAT_WSS;

const isWebitelEnabled = (): boolean =>
  (isProduction || isStaging) && Boolean(WEBITEL_CHAT_URL) && Boolean(WEBITEL_CHAT_WSS);

const loadStylesheetOnce = (href: string): void => {
  if (document.querySelector(`link[href="${href}"]`)) {
    return;
  }

  const link = document.createElement('link');
  link.href = href;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

const getWebitelConfig = () => ({
  view: {
    borderRadiusStyle: 'rounded',
    lang: 'en',
    btnOpacity: '1',
    logoUrl: 'https://prestamo365.pe/wp-content/uploads/2025/01/logo.svg',
    accentColor: 'hsl(251, 80%, 60%)',
    position: 'right',
  },
  chat: {
    url: WEBITEL_CHAT_WSS,
  },
});

const setWidgetVisibility = (isVisible: boolean): void => {
  const widgetEl = document.getElementById(WEBITEL_WIDGET_ID);
  if (!widgetEl) {
    return;
  }

  widgetEl.style.display = isVisible ? '' : 'none';
};

export const useWebitelWidget = (): void => {
  const { pathname } = useLocation();
  const isInitializedRef = useRef(false);
  const widgetInstanceRef = useRef<{ destroy?: () => void } | null>(null);
  const isLoginPage = pathname === RouterUrl.Login;

  useEffect(() => {
    if (!isWebitelEnabled()) {
      return;
    }

    if (isLoginPage) {
      setWidgetVisibility(false);
      return;
    }

    let isCancelled = false;

    const initWidget = async (): Promise<void> => {
      try {
        loadStylesheetOnce(`${WEBITEL_CHAT_URL}/WtOmniWidget.css`);
        await loadScriptOnce(`${WEBITEL_CHAT_URL}/WtOmniWidget.umd.js`);

        if (isCancelled) {
          return;
        }

        if (isInitializedRef.current) {
          setWidgetVisibility(true);
          return;
        }

        const widgetEl = document.createElement('div');
        widgetEl.id = WEBITEL_WIDGET_ID;
        document.body.appendChild(widgetEl);
        widgetInstanceRef.current = new window.WtOmniWidget(`#${WEBITEL_WIDGET_ID}`, getWebitelConfig());
        isInitializedRef.current = true;
        setWidgetVisibility(true);
      } catch (error) {
        console.error('Failed to load Webitel widget', error);
      }
    };

    void initWidget();

    return () => {
      isCancelled = true;
    };
  }, [isLoginPage]);
};
